import { useEffect, useState } from "react";
import { ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import { PageHero, Section, Eyebrow, P } from "@/components/SharedUI";
import { getConsent, grantConsent, denyConsent } from "@/lib/analytics";

export default function PrivacyPreferences() {
  const [consent, setConsent] = useState(getConsent());

  useEffect(() => {
    setConsent(getConsent());
  }, []);

  const handleGrant = () => {
    grantConsent();
    setConsent("granted");
  };
  const handleDeny = () => {
    denyConsent();
    setConsent("denied");
  };

  const state = consent === "granted" ? "on" : consent === "denied" ? "off" : "undecided";

  return (
    <>
      <PageHero
        eyebrow="Your data, your choice"
        title="Privacy preferences"
        desc="We respect Indigenous Data Sovereignty. Choose whether anonymised analytics may help us understand site engagement."
      />

      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <Eyebrow>Analytics</Eyebrow>
              <div className="mt-2 inline-flex items-center gap-2 border border-stone-300 bg-stone-50 px-3 py-2">
                {state === "on" && (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                    <span className="text-sm font-semibold text-stone-900" data-testid="privacy-status">Analytics ON</span>
                  </>
                )}
                {state === "off" && (
                  <>
                    <XCircle className="h-4 w-4 text-stone-500" />
                    <span className="text-sm font-semibold text-stone-900" data-testid="privacy-status">Analytics OFF</span>
                  </>
                )}
                {state === "undecided" && (
                  <>
                    <ShieldCheck className="h-4 w-4 text-stone-500" />
                    <span className="text-sm font-semibold text-stone-900" data-testid="privacy-status">Not chosen yet</span>
                  </>
                )}
              </div>

              <P className="mt-6">
                We use Google Analytics 4 with IP anonymisation enabled. We do not collect names, emails, exact locations, advertising identifiers or any sensitive personal data. Aggregated traffic patterns help us understand which sections of the site are useful to procurement teams, community partners and government agencies.
              </P>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleGrant}
                  data-testid="privacy-grant"
                  className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-3 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors"
                >
                  <CheckCircle2 className="h-4 w-4" /> Turn analytics ON
                </button>
                <button
                  type="button"
                  onClick={handleDeny}
                  data-testid="privacy-deny"
                  className="inline-flex items-center gap-2 border border-stone-300 px-5 py-3 text-sm font-medium rounded-sm hover:border-stone-900 transition-colors text-stone-900"
                >
                  <XCircle className="h-4 w-4" /> Turn analytics OFF
                </button>
              </div>
            </div>

            <aside className="lg:col-span-5">
              <div className="border border-stone-200 bg-stone-50 p-6">
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500 mb-3">
                  What we track
                </div>
                <ul className="space-y-2 text-sm text-stone-700 leading-relaxed">
                  {[
                    "Page views (which pages, how long)",
                    "Country & device type (high-level only)",
                    "Referral source (e.g. LinkedIn, search)",
                    "Aggregate event counts (e.g. PDF downloaded)",
                  ].map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="mt-2 h-1 w-1 bg-[#9C4221] rounded-full shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="mt-6 text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500 mb-3">
                  What we never track
                </div>
                <ul className="space-y-2 text-sm text-stone-700 leading-relaxed">
                  {[
                    "Your name, email, phone or address",
                    "Cultural material, community data or IDS-sensitive content",
                    "Exact location, precise IP, biometrics",
                    "Any data sold to advertisers or third parties",
                  ].map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="mt-2 h-1 w-1 bg-stone-400 rounded-full shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </Section>
      </section>
    </>
  );
}
